import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    ArrowLeft,
    BatteryCharging,
    Check,
    Info,
    RotateCcw,
    Share2
} from 'lucide-react'

import { cn } from '@/lib/utils'

// The four recovery experiences described by Sonnentag and Fritz (2007). The
// statements below are our own reflection prompts, NOT items from their Recovery
// Experience Questionnaire, and the tool page says the quiz is not validated.
type RestType = 'detachment' | 'relaxation' | 'mastery' | 'control'

interface Question {
    type: RestType
    text: string
}

interface Outcome {
    title: string
    summary: string
    ideas: string[]
    readHref: string
    readLabel: string
}

// Two statements per experience, interleaved so the pattern is not obvious.
const QUESTIONS: Question[] = [
    { type: 'detachment', text: 'Hours after work, I am still replaying conversations or tasks from the day.' },
    { type: 'relaxation', text: 'I feel wired or tense in the evening, even when I am tired.' },
    { type: 'mastery', text: 'Outside work, I rarely do anything that challenges me or teaches me something new.' },
    { type: 'control', text: 'Most of my day is decided by other people’s requests and deadlines.' },
    { type: 'detachment', text: 'I check work messages in the evening or on days off, even when nothing is urgent.' },
    { type: 'relaxation', text: 'By the end of the day, noise, screens and people feel like too much.' },
    { type: 'mastery', text: 'My free time feels flat or samey, even when it is restful.' },
    { type: 'control', text: 'Even my free time feels full of obligations rather than choices.' }
]

const ANSWERS = [
    { label: 'Not like me', value: 0 },
    { label: 'Sometimes', value: 1 },
    { label: 'Very like me', value: 2 }
]

const MAX_PER_TYPE = 4

const ORDER: RestType[] = ['detachment', 'relaxation', 'mastery', 'control']

const NAMES: Record<RestType, string> = {
    detachment: 'Detachment',
    relaxation: 'Relaxation',
    mastery: 'Mastery',
    control: 'Control'
}

const OUTCOMES: Record<RestType, Outcome> = {
    detachment: {
        title: 'You need detachment',
        summary:
            'Your body has left work but your attention has not. Rest that leaves your mind free to wander back to the day will not fix this. You need something absorbing enough to crowd work out.',
        ideas: [
            'Pick something that takes your full attention: a film, a game, cooking a new recipe, a proper conversation.',
            'Close work apps on your phone at a set time, not just the laptop.',
            'Write tomorrow’s first task down before you stop, so your brain can let go of it.'
        ],
        readHref: '/work-life-balance/switching-off-from-work',
        readLabel: 'Why switching off matters more than time off'
    },
    relaxation: {
        title: 'You need relaxation',
        summary:
            'You are running hot. The fix is less input, not different input: lower the noise, slow down and give your body a chance to settle before you ask it to sleep.',
        ideas: [
            'Try five minutes of slow breathing with the guided timer.',
            'Swap one screen session for a slow walk, a bath or quiet music.',
            'Dim the lights and put your phone in another room for the last hour of the evening.'
        ],
        readHref: '/breathwork/guided-breathing-timer',
        readLabel: 'Try the guided breathing timer'
    },
    mastery: {
        title: 'You need mastery',
        summary:
            'Your rest is easy but it is not refilling anything. Effort can be restful when it builds something: a skill, a small win, a sense of getting better at a thing nobody is grading.',
        ideas: [
            'Choose one thing you are not yet good at and give it 20 minutes.',
            'Pick a challenge with visible progress: a language app streak, a harder walk, a recipe you have never tried.',
            'Keep it low-stakes. The point is progress, not performance.'
        ],
        readHref: '/work-life-balance/types-of-rest',
        readLabel: 'Rest is not one thing: the four kinds of recovery'
    },
    control: {
        title: 'You need control',
        summary:
            'Your time is being decided for you. What you do in your free time matters less than the fact that you chose it, so protect some hours that belong to nobody else.',
        ideas: [
            'Block one evening this week that nobody else gets to book.',
            'Say no to one optional obligation and use the time however you like.',
            'At work, ask for a say in when you take breaks or how you pace a task.'
        ],
        readHref: '/work-life-balance/what-is-work-life-balance',
        readLabel: 'What work-life balance actually means'
    }
}

// Below this, no experience stands out as a clear shortfall.
const LOW_SCORE = 1

type Scores = Record<RestType, number>

const tally = (answers: number[]): Scores => {
    const scores: Scores = { detachment: 0, relaxation: 0, mastery: 0, control: 0 }
    answers.forEach((value, index) => {
        scores[QUESTIONS[index].type] += value
    })
    return scores
}

export const RestQuiz: FC = (): ReactNode => {
    const [answers, setAnswers] = useState<number[]>([])
    const [shared, setShared] = useState(false)

    const headingRef = useRef<HTMLSpanElement>(null)
    // Focus only moves after the visitor has answered something, never on the
    // first render, so the page does not jump to the quiz on load.
    const interacted = useRef(false)

    const step = answers.length
    const finished = step >= QUESTIONS.length

    useEffect(() => {
        if (interacted.current) headingRef.current?.focus()
    }, [step])

    const answer = (value: number) => {
        interacted.current = true
        setAnswers((previous) => [...previous, value])
    }

    const back = () => {
        interacted.current = true
        setAnswers((previous) => previous.slice(0, -1))
    }

    const restart = () => {
        interacted.current = true
        setShared(false)
        setAnswers([])
    }

    const scores = tally(answers)
    const top = Math.max(...ORDER.map((type) => scores[type]))
    const leaders = ORDER.filter((type) => scores[type] === top)
    const settled = finished && top <= LOW_SCORE

    const share = async () => {
        const url = `${window.location.origin}${window.location.pathname}`
        const label = leaders.map((type) => NAMES[type]).join(' and ')
        const text = settled
            ? 'My rest seems to be working. What kind of rest do you need?'
            : `The kind of rest I need most: ${label}. What kind do you need?`

        try {
            if (navigator.share) {
                await navigator.share({ title: 'What kind of rest do you need?', text, url })
            } else {
                await navigator.clipboard.writeText(`${text} ${url}`)
                setShared(true)
            }
        } catch {
            // Dismissing the share sheet rejects the promise. Nothing to do.
        }
    }

    return (
        <section
            aria-label='What kind of rest do you need? Quiz'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <BatteryCharging className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        What kind of rest do you need?
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Rate {QUESTIONS.length} short statements for how your evenings and days off have
                    felt lately, then see your result.
                </span>
                <span className='mt-3 flex flex-wrap gap-2'>
                    <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>
                        {QUESTIONS.length} questions
                    </span>
                    <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>
                        About 1 minute
                    </span>
                    <span className='rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground'>
                        One tap per answer
                    </span>
                </span>
            </div>

            <div className='px-5 py-6 sm:px-6'>
                {!finished && (
                    <div>
                        <div className='flex items-center justify-between gap-3'>
                            <span className='text-sm font-semibold text-primary'>
                                Question {step + 1} of {QUESTIONS.length}
                            </span>
                            {step > 0 && (
                                <button
                                    type='button'
                                    onClick={back}
                                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    <ArrowLeft className='size-4' />
                                    Previous question
                                </button>
                            )}
                        </div>

                        <div
                            className='mt-3 grid gap-1.5'
                            style={{ gridTemplateColumns: `repeat(${QUESTIONS.length}, minmax(0, 1fr))` }}
                            role='progressbar'
                            aria-valuemin={0}
                            aria-valuemax={QUESTIONS.length}
                            aria-valuenow={step}
                            aria-valuetext={`Question ${step + 1} of ${QUESTIONS.length}`}
                            aria-label='Quiz progress'
                        >
                            {QUESTIONS.map((question, index) => (
                                <span
                                    key={question.text}
                                    className={cn(
                                        'h-2 rounded-full transition-colors duration-300',
                                        index < step
                                            ? 'bg-primary'
                                            : index === step
                                                ? 'bg-primary/40'
                                                : 'bg-secondary'
                                    )}
                                />
                            ))}
                        </div>

                        <span className='mt-6 block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground'>
                            How true is this for you lately?
                        </span>

                        <span
                            ref={headingRef}
                            tabIndex={-1}
                            className='mt-2 block font-heading text-xl font-semibold leading-snug tracking-tight text-foreground outline-none sm:text-2xl'
                        >
                            {QUESTIONS[step].text}
                        </span>

                        <div role='group' aria-label='Your answer' className='mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3'>
                            {ANSWERS.map((option) => (
                                <button
                                    key={option.value}
                                    type='button'
                                    onClick={() => answer(option.value)}
                                    className='rounded-xl border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>

                        <span className='mt-4 block text-xs text-muted-foreground'>
                            {step + 1 < QUESTIONS.length
                                ? `Pick an answer to go to question ${step + 2} of ${QUESTIONS.length}.`
                                : 'Last one. Pick an answer to see your result.'}
                        </span>
                    </div>
                )}

                {finished && (
                    <div>
                        <span
                            ref={headingRef}
                            tabIndex={-1}
                            className='block text-xs font-semibold uppercase tracking-[0.14em] text-primary outline-none'
                        >
                            Your result
                        </span>

                        {settled ? (
                            <div className='mt-3'>
                                <span className='block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                    Your rest seems to be working
                                </span>
                                <span className='mt-2 block text-sm leading-relaxed text-muted-foreground'>
                                    None of the four kinds of recovery stands out as missing right now. If
                                    that changes, come back and take it again: the answer often shifts
                                    with a busy week.
                                </span>
                            </div>
                        ) : (
                            <div className='mt-3 flex flex-col gap-5'>
                                {leaders.length > 1 && (
                                    <span className='block text-sm text-muted-foreground'>
                                        More than one kind of rest came out level, so each is shown below.
                                    </span>
                                )}

                                {leaders.map((type) => {
                                    const outcome = OUTCOMES[type]
                                    return (
                                        <div key={type} className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                            <span className='block font-heading text-2xl font-semibold tracking-tight text-foreground'>
                                                {outcome.title}
                                            </span>
                                            <span className='mt-2 block text-sm leading-relaxed text-muted-foreground'>
                                                {outcome.summary}
                                            </span>
                                            <span className='mt-4 block text-sm font-semibold text-foreground'>
                                                Try this week
                                            </span>
                                            <span className='mt-2 flex flex-col gap-2'>
                                                {outcome.ideas.map((idea) => (
                                                    <span key={idea} className='flex gap-2 text-sm leading-relaxed text-muted-foreground'>
                                                        <Check className='mt-0.5 size-4 shrink-0 text-primary' />
                                                        {idea}
                                                    </span>
                                                ))}
                                            </span>
                                            <Link
                                                href={outcome.readHref}
                                                className='mt-4 inline-block text-sm font-medium text-primary no-underline hover:underline'
                                            >
                                                {outcome.readLabel}
                                            </Link>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        <span className='mt-6 block text-sm font-semibold text-foreground'>All four scores</span>
                        <span className='mt-3 flex flex-col gap-3'>
                            {ORDER.map((type) => (
                                <span key={type} className='grid grid-cols-[6.5rem_1fr_2.5rem] items-center gap-3 text-sm'>
                                    <span className='text-foreground'>{NAMES[type]}</span>
                                    <span className='h-2 overflow-hidden rounded-full bg-secondary'>
                                        <span
                                            className={cn(
                                                'block h-full rounded-full',
                                                leaders.includes(type) && !settled ? 'bg-primary' : 'bg-primary/40'
                                            )}
                                            style={{ width: `${(scores[type] / MAX_PER_TYPE) * 100}%` }}
                                        />
                                    </span>
                                    <span className='text-right tabular-nums text-muted-foreground'>
                                        {scores[type]}/{MAX_PER_TYPE}
                                    </span>
                                </span>
                            ))}
                        </span>

                        <div className='mt-6 flex flex-wrap items-center gap-3'>
                            <button
                                type='button'
                                onClick={share}
                                className='inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            >
                                <Share2 className='size-4' />
                                {shared ? 'Link copied' : 'Share my result'}
                            </button>
                            <button
                                type='button'
                                onClick={restart}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            >
                                <RotateCcw className='size-4' />
                                Take it again
                            </button>
                        </div>
                    </div>
                )}

                <div className='mt-6 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        A reflection prompt, not a clinical test. It cannot tell you whether you have
                        burnout or anything else, and your answers are not stored.
                    </span>
                </div>
            </div>
        </section>
    )
}

export default RestQuiz
